import React, { useState } from 'react';
import { Download } from 'lucide-react';
import QualityBreakdown from './QualityBreakdown';

import { getPrompt } from '../../services/evaluationService';
import ToastDownload, {stateNames as toastDownloadStateNames} from "../common/Toast";
import { qualityScores } from '../../utils/qualityConstants';
import usePdfDownload from "../../hooks/usePdfDownload";
import viewPromptNew from '../../assets/icons/view_prompt_new.svg';
import PromptModal from '../common/PromptModal';
import GuidelinesModal from '../common/GuidelinesModal';

const SummaryPanel = ({ evaluation, segments, listModels, isSkeletonLoader }) => {
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [isGuidelinesModalOpen, setIsGuidelinesModalOpen] = useState(false);

  const {
    downloadEvaluatedFile,
    onHandleDownloadEvaluatedFile,
    downloadPdfState,
    pdfBlob
  } = usePdfDownload();

  // const totalTokens = (evaluation?.tokensInUsed || 0) + (evaluation?.tokensOutUsed || 0);

  const getQualityCategories = (evaluation) => {
    const statistics = evaluation?.statistics || {};
    const totalSegments = statistics?.totalSegments || 0;
    const completedMinusError = statistics.error > 0 ? 0 : Math.abs(statistics.completed - statistics.error);
    const wontProcess = completedMinusError === 0 ? 0 : Math.max(totalSegments - completedMinusError, 0);

    // Map backend counts to the new categories
    const publishReadyCount = statistics.publishReady || 0;
    const flawedCount = (statistics.acceptable || 0) || 0; // merge acceptable + fair
    const unusableCount = statistics.unacceptable + (statistics.fair || 0); // remains unusable

    return [
      {
        label: qualityScores[0].label,
        amount: publishReadyCount,
        percentage:
          statistics.completed > 0
            ? Math.round((publishReadyCount / statistics.completed) * 100)
            : 0,
        color: qualityScores[0].color.replace('text-', 'bg-'),
        total: statistics.completed,
      },
      {
        label: qualityScores[1].label,
        amount: flawedCount,
        percentage:
          statistics.completed > 0
            ? Math.round((flawedCount / statistics.completed) * 100)
            : 0,
        color: qualityScores[1].color.replace('text-', 'bg-'),
        total: statistics.completed,
      },
      {
        label: qualityScores[2].label,
        amount: unusableCount,
        percentage:
          statistics.completed > 0
            ? Math.round((unusableCount / statistics.completed) * 100)
            : 0,
        color: qualityScores[2].color.replace('text-', 'bg-'),
        total: statistics.completed,
      },
      {
        label: qualityScores[3].label,
        amount: statistics.error + wontProcess,
        color: qualityScores[3].color.replace('text-', 'bg-'),
        percentage:
          statistics.completed > 0
            ? Math.round(((statistics.error + wontProcess) / totalSegments) * 100)
            : statistics.error > 0 || wontProcess > 0
              ? 100
              : 0,
        total:
          statistics.completed > 0
            ? totalSegments
            : statistics.error > 0 || wontProcess > 0
              ? Math.max(statistics.error, 0) + Math.max(wontProcess, 0)
              : 0,
      },
    ];
  };

  const overallScore = evaluation?.overallScore || 0;
  const formattedOverallScore = overallScore.toFixed();
  const scoreDescription = (() => {
    if (overallScore >= 91) return qualityScores[0].description; // Publish-Ready
    if (overallScore >= 70) return qualityScores[1].description; // Flawed
    if (overallScore >= 1) return qualityScores[2].description; // Unusable (1-69)
    return qualityScores[3].description; // Error/Not processed (0)
  })();
  const totalCharacters = evaluation?.overallScore ? segments?.reduce((acc, segment) => 
    acc + (segment.sourceText?.length || 0) + (segment.targetText?.length || 0), 0) || 0 : 0;

  const handlePromptClick = async () => {
    if (!evaluation?.model) return;
    
    setIsPromptModalOpen(true);
    setIsLoadingPrompt(true);

    try {
      const promptResponse = await getPrompt(evaluation.model);
      setPromptText(`${promptResponse.evaluationPrompt}`)
    } catch (error) {
      console.error('Error fetching prompt:', error);
      setPromptText('Error loading prompt');
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const handleGuidelinesClick = () => {
    setIsGuidelinesModalOpen(true);
  };

  const selectedModel = listModels?.find(model => model.id === evaluation?.model);

  const skeletonStyles = "bg-[#c1c1c1] text-transparent rounded animate-pulse min-w-[35px] w-[fit-content]";

  return (
    <div className="bg-white border rounded-lg p-4 md:p-6 w-full">
      <h2 className="text-lg font-semibold mb-6">Summary</h2>

      <div className="space-y-6">
        <div>
          <div className={`text-3xl font-bold mb-2 ${isSkeletonLoader && skeletonStyles}`}>{formattedOverallScore}/100</div>
          <div className="text-sm text-gray-500 mb-6">{scoreDescription}</div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Segments Breakdown
          </h3>
          <QualityBreakdown categories={getQualityCategories(evaluation)} isSkeletonLoader={isSkeletonLoader} skeletonStyles={skeletonStyles}/>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Model used:</span>
            <span className={`font-medium ${isSkeletonLoader && skeletonStyles}`}>
              {selectedModel?.displayName || 'N/A'}
            </span>
          </div>
          <div className="flex justify-between items-center !mt-3">
            <span className="text-gray-600">Prompt used:</span>
            <p className={`flex items-center gap-1 cursor-default text-[#3B82F6] font-medium ${isSkeletonLoader && skeletonStyles}`}>
            View
             {!isSkeletonLoader && <button 
                onClick={handlePromptClick}
                className={`
                  flex items-center justify-center 
                  w-4 h-4 border-none bg-[#F1F5F9] hover:bg-[#CAD8E6] shadow-[0px_1px_2px_0px_#0000000D]
                  disabled:cursor-not-allowed p-0 m-0 rounded-[4px] 
                  ${isSkeletonLoader && skeletonStyles}
                `}
                disabled={!evaluation?.model}
              >
              <img src={viewPromptNew} alt="View" className="w-3 h-3" />
            </button>}
            </p>
          </div>          
          <div className="flex justify-between items-center !mt-3">
            <span className="text-gray-600">Guidelines:</span>
             <p className={`flex items-center gap-1 cursor-default text-[#3B82F6] font-medium ${isSkeletonLoader && skeletonStyles}`}>
              View
              {!isSkeletonLoader && <button 
                onClick={handleGuidelinesClick}
                className={`flex items-center justify-center 
                  w-4 h-4 border-none bg-[#F1F5F9] hover:bg-[#CAD8E6] shadow-[0px_1px_2px_0px_#0000000D]
                  p-0 m-0 rounded-[4px] ${isSkeletonLoader && skeletonStyles}`}
              >
              <img src={viewPromptNew} alt="View" className="w-3 h-3" />
            </button>}
            </p>
          </div>
          <div className="flex justify-between !mt-3">
            <span className="text-gray-600">Total segments:</span>
            <span className={`font-medium ${isSkeletonLoader && skeletonStyles}`}>{evaluation?.statistics?.totalSegments || 0}</span>
          </div>
          <div className="flex justify-between !mt-3">
            <span className="text-gray-600">Total characters:</span>
            <span className={`font-medium ${isSkeletonLoader && skeletonStyles}`}>{totalCharacters}</span>
          </div>
          {/* <div className="flex justify-between">
            <span className="text-gray-600">Total tokens used:</span>
            <span className={`font-medium ${isSkeletonLoader && skeletonStyles}`}>{totalTokens || 0}</span>
          </div> */}
        </div>

        {evaluation?.id ? (
          <div
            onClick={() => !downloadPdfState && onHandleDownloadEvaluatedFile(evaluation.id)}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-[rgb(17,24,39)] hover:bg-[rgb(17,24,39)]/90 text-white rounded-md transition-colors cursor-pointer"
          >
            <Download size={16} />
            <span>Download Summary (PDF)</span>
          </div>
        ) : (
          <button
            disabled
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-[rgb(17,24,39)] hover:bg-[rgb(17,24,39)]/90 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[rgb(17,24,39)]"
          >
            <Download size={16} />
            <span>Download Summary (PDF)</span>
          </button>
        )}
      </div>
      {
        downloadPdfState &&
          <ToastDownload
              stateName={downloadPdfState}
              linkAction={
                downloadPdfState === toastDownloadStateNames.success ?
                    () => downloadEvaluatedFile(pdfBlob, evaluation?.id) :
                    () => onHandleDownloadEvaluatedFile(evaluation?.id)
              }
          />
      }
      <PromptModal 
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        prompt={isLoadingPrompt ? 'Loading prompt...' : promptText}
      />
      <GuidelinesModal
        isOpen={isGuidelinesModalOpen}
        onClose={() => setIsGuidelinesModalOpen(false)}
        initialText={evaluation?.guidelines}
        showSaveButton={false}
      />
    </div>
  );
};

export default SummaryPanel; 